import { Suspense, useState, useRef } from "react";
import { Canvas, useFrame, type ThreeEvent } from "@react-three/fiber";
import { OrbitControls, ContactShadows, Billboard, Text } from "@react-three/drei";
import * as THREE from "three";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Animal3DModel } from "./Animal3DModel";
import { useDressupStore } from "../../store/dressup";
import { useAuthStore } from "../../store/auth";
import type { AreaDetail, SectionSummary, UnitSummary } from "../../content/lesson";
import type { AreaId } from "../../content/map";
import { AREA_BY_ID } from "../../content/map";

type Props = {
  areaData: AreaDetail;
  areaId: AreaId;
  onOpenUnit: (sectionId: string, unitIndex: number) => void;
};

// =============================================================================
// エリアごとの大阪ことば・生活トリビア
// =============================================================================
const OSAKA_CULTURE_NOTES: Record<AreaId, { phrase: string; noteJa: string; noteEn: string }> = {
  airport: {
    phrase: "まいど！ 空港へようこそ",
    noteJa: "空港に着いたらまずは『こんにちは』から！ 関空は海に浮かぶ島やで 🛫",
    noteEn: "Start with 'Konnichiwa' at the airport! KIX is built on a man-made island 🛫",
  },
  home: {
    phrase: "ただいま〜！ おかえり！",
    noteJa: "おうちでは『ただいま』『おかえり』の挨拶が温かい生活の第一歩や 🏠",
    noteEn: "'Tadaima' (I'm home) and 'Okaeri' (welcome back) start daily life at home 🏠",
  },
  school: {
    phrase: "おはようさん！ 今日もがんばろ",
    noteJa: "教室に入ったら元気にあいさつ！ 先生やクラスメイトと仲良くなろな 🏫",
    noteEn: "Greet everyone cheerfully! Make friends with classmates and teachers 🏫",
  },
  shop: {
    phrase: "温めますか？『お願いします』",
    noteJa: "コンビニでは『これお願いします』『大丈夫です』が言えたら一人前や 🍙",
    noteEn: "Say 'Kore onegaishimasu' (This please) at convenience store counters 🍙",
  },
  cafe: {
    phrase: "アイスコーヒーひとつ！",
    noteJa: "『○○をひとつ、お願いします』で注文はバッチリ！ 落ち着いて話そ ☕",
    noteEn: "Order smoothly with '[Item] o hitotsu onegaishimasu' ☕",
  },
  station: {
    phrase: "○○行きは どのホームですか？",
    noteJa: "電車の乗り換えで迷ったら、駅員さんに『すみません』と尋ねてみよ 🚉",
    noteEn: "If lost at the station, ask staff with 'Sumimasen, [Station] wa doko desu ka?' 🚉",
  },
  library: {
    phrase: "静かに本を読もうな",
    noteJa: "『この本を借りたいです』と言って図書カードを出してみよ 📚",
    noteEn: "Use 'Kono hon o karitai desu' (I'd like to borrow this book) at the counter 📚",
  },
  park: {
    phrase: "ええ天気やな〜！",
    noteJa: "公園のベンチでひと休み。近所の人に会ったら『こんにちは』と会釈や 🌸",
    noteEn: "Relax on a bench. Greet neighbors gently with a small bow and 'Konnichiwa' 🌸",
  },
  friendhouse: {
    phrase: "おじゃまします！ これ手土産",
    noteJa: "人の家に入るときは『おじゃまします』、靴は揃えてあがろうな 🎁",
    noteEn: "Always say 'Ojamashimasu' when entering someone's home and align your shoes 🎁",
  },
  hospital: {
    phrase: "お大事にどうぞ",
    noteJa: "『頭が痛いです』『熱があります』、体の調子をしっかり伝えるのが大切 🏥",
    noteEn: "Explain your symptoms clearly: 'Atama ga itai desu' (I have a headache) 🏥",
  },
  cityoffice: {
    phrase: "番号札をお取りください",
    noteJa: "手続きの窓口では『住所の登録をお願いします』と書類を渡そ 🏢",
    noteEn: "Take a number ticket and hand documents politely at municipal counters 🏢",
  },
  workplace: {
    phrase: "お疲れ様です！ お先に失礼します",
    noteJa: "バイト先では『お疲れ様です』が合言葉！ 報告と挨拶をしっかりと 👔",
    noteEn: "'Otsukaresama desu' is the universal workplace greeting. Report clearly 👔",
  },
};

// =============================================================================
// 1. 生徒の3Dアバター（Click-to-Moveで移動）
// =============================================================================
function StudentAvatar({
  targetPos,
  currentPos,
  studentName,
  onArrived,
}: {
  targetPos: [number, number, number];
  currentPos: React.MutableRefObject<[number, number, number]>;
  studentName: string;
  onArrived: () => void;
}) {
  const store = useDressupStore();
  const groupRef = useRef<THREE.Group>(null);
  const modelRef = useRef<THREE.Group>(null);
  const [isWalking, setIsWalking] = useState(false);
  const [isJumping, setIsJumping] = useState(false);

  useFrame((_, delta) => {
    if (!groupRef.current || !modelRef.current) return;

    const [tx, , tz] = targetPos;
    const [cx, , cz] = currentPos.current;

    const dx = tx - cx;
    const dz = tz - cz;
    const dist = Math.hypot(dx, dz);

    if (dist > 0.08) {
      if (!isWalking) setIsWalking(true);
      const speed = 3.6 * delta;
      const step = Math.min(speed, dist);
      const angle = Math.atan2(dx, dz);

      currentPos.current[0] += Math.sin(angle) * step;
      currentPos.current[2] += Math.cos(angle) * step;

      groupRef.current.position.x = currentPos.current[0];
      groupRef.current.position.z = currentPos.current[2];

      if (Math.abs(dx) > 0.05) {
        modelRef.current.scale.x = dx > 0 ? 1 : -1;
      }
      modelRef.current.rotation.y = Math.sin(angle) * 0.25;
    } else {
      if (isWalking) {
        setIsWalking(false);
        setIsJumping(true);
        onArrived();
      }
    }
  });

  return (
    <group ref={groupRef} position={currentPos.current}>
      <group ref={modelRef}>
        <Animal3DModel
          animalType={store.animalType}
          headwear={store.headwear}
          outfit={store.outfit}
          accessory={store.accessory}
          colorTint={store.colorTint}
          walking={isWalking}
          walkSpeed={14}
          jumping={isJumping}
          onJumpComplete={() => setIsJumping(false)}
        />
      </group>
      {/* 生徒アバターの頭上ネームタグ */}
      <Billboard position={[0, 1.45, 0]}>
        <Text
          fontSize={0.22}
          color="#8f332c"
          anchorX="center"
          anchorY="middle"
          outlineWidth={0.035}
          outlineColor="#ffffff"
        >
          {`${studentName || "生徒"} さん 🎓`}
        </Text>
      </Billboard>
    </group>
  );
}

// =============================================================================
// 2. レッスンクリスタルピン（Unit Pin）
// =============================================================================
function LessonPin({
  unit,
  position,
  isCleared,
  isCurrent,
  isLocked,
  onSelect,
}: {
  unit: UnitSummary;
  position: [number, number, number];
  isCleared: boolean;
  isCurrent: boolean;
  isLocked: boolean;
  onSelect: () => void;
}) {
  const [hovered, setHovered] = useState(false);
  const crystalRef = useRef<THREE.Mesh>(null);
  const ringRef = useRef<THREE.Mesh>(null);

  useFrame((_, delta) => {
    if (crystalRef.current) {
      crystalRef.current.rotation.y += delta * (isCurrent ? 1.8 : 0.6);
      if (isCurrent) {
        crystalRef.current.position.y = position[1] + 0.45 + Math.sin(Date.now() * 0.004) * 0.08;
      }
    }
    if (ringRef.current && isCurrent) {
      ringRef.current.rotation.z += delta * 1.2;
    }
  });

  const baseColor = isCleared ? "#10b981" : isCurrent ? "#ea580c" : "#94a3b8";

  return (
    <group position={position}>
      {/* 台座 */}
      <mesh position={[0, 0.05, 0]} receiveShadow>
        <cylinderGeometry args={[0.5, 0.6, 0.1, 16]} />
        <meshStandardMaterial color={isCleared ? "#d1fae5" : isCurrent ? "#ffedd5" : "#f1f5f9"} />
      </mesh>

      {/* 現在地パルスリング */}
      {isCurrent && (
        <mesh ref={ringRef} position={[0, 0.12, 0]} rotation={[-Math.PI / 2, 0, 0]}>
          <ringGeometry args={[0.65, 0.75, 24]} />
          <meshBasicMaterial color="#f97316" transparent opacity={0.6} side={THREE.DoubleSide} />
        </mesh>
      )}

      {/* クリスタル本体 */}
      <mesh
        ref={crystalRef}
        position={[0, 0.45, 0]}
        castShadow
        onClick={(e) => {
          e.stopPropagation();
          if (!isLocked) onSelect();
        }}
        onPointerEnter={() => !isLocked && setHovered(true)}
        onPointerLeave={() => setHovered(false)}
        scale={hovered ? 1.25 : 1}
      >
        <octahedronGeometry args={[0.3, 0]} />
        <meshStandardMaterial
          color={baseColor}
          emissive={isCurrent ? "#f97316" : isCleared ? "#059669" : "#475569"}
          emissiveIntensity={isCurrent ? 0.6 : isCleared ? 0.3 : 0.1}
          roughness={0.25}
          metalness={0.4}
        />
      </mesh>

      {/* 頭上ピン番号・状態バッジ */}
      <Billboard position={[0, 1.0, 0]}>
        <Text
          fontSize={0.22}
          color={isLocked ? "#64748b" : "#1e293b"}
          outlineWidth={0.035}
          outlineColor="#ffffff"
          anchorX="center"
          anchorY="middle"
        >
          {isCleared ? `⭐ U${unit.unit_index} CLEAR` : isCurrent ? `🎯 U${unit.unit_index} START` : `🔒 U${unit.unit_index}`}
        </Text>
      </Billboard>
    </group>
  );
}

// =============================================================================
// 3. セクション区切りランドマーク看板
// =============================================================================
function SectionHeaderSign({
  position,
  title,
  index,
  cleared,
}: {
  position: [number, number, number];
  title: string;
  index: number;
  cleared: boolean;
}) {
  return (
    <group position={position}>
      {/* 看板柱 */}
      <mesh position={[-0.9, 0.6, 0]} castShadow>
        <cylinderGeometry args={[0.04, 0.05, 1.2, 8]} />
        <meshStandardMaterial color="#78350f" />
      </mesh>
      <mesh position={[0.9, 0.6, 0]} castShadow>
        <cylinderGeometry args={[0.04, 0.05, 1.2, 8]} />
        <meshStandardMaterial color="#78350f" />
      </mesh>
      {/* 看板板 */}
      <mesh position={[0, 0.95, 0]} castShadow>
        <boxGeometry args={[2.0, 0.6, 0.1]} />
        <meshStandardMaterial color={cleared ? "#ecfdf5" : "#fffbeb"} roughness={0.5} />
      </mesh>
      <Billboard position={[0, 0.95, 0.07]}>
        <Text
          fontSize={0.16}
          color={cleared ? "#047857" : "#8f332c"}
          outlineWidth={0.02}
          outlineColor="#ffffff"
          anchorX="center"
          anchorY="middle"
        >
          {`第${index}部: ${title}`}
        </Text>
      </Billboard>
    </group>
  );
}

// =============================================================================
// 4. 住民NPC（温かく迎える案内役）
// =============================================================================
function ResidentNPC({
  position,
  name,
  icon,
  onTalk,
}: {
  position: [number, number, number];
  name: string;
  icon: string;
  onTalk: () => void;
}) {
  const [hovered, setHovered] = useState(false);
  const meshRef = useRef<THREE.Group>(null);

  useFrame(() => {
    if (meshRef.current) {
      meshRef.current.position.y = position[1] + Math.sin(Date.now() * 0.003) * 0.05;
    }
  });

  return (
    <group
      ref={meshRef}
      position={position}
      onClick={(e) => {
        e.stopPropagation();
        onTalk();
      }}
      onPointerEnter={() => setHovered(true)}
      onPointerLeave={() => setHovered(false)}
    >
      {/* NPCボディ */}
      <mesh position={[0, 0.45, 0]} castShadow>
        <capsuleGeometry args={[0.26, 0.35, 4, 12]} />
        <meshStandardMaterial color={hovered ? "#fbbf24" : "#f59e0b"} roughness={0.4} />
      </mesh>

      {/* 頭上案内 */}
      <Billboard position={[0, 1.3, 0]}>
        <Text
          fontSize={0.24}
          color="#8f332c"
          outlineWidth={0.04}
          outlineColor="#ffffff"
          anchorX="center"
          anchorY="middle"
        >
          {`${icon} ${name}`}
        </Text>
        <Text
          position={[0, -0.26, 0]}
          fontSize={0.15}
          color="#0284c7"
          outlineWidth={0.03}
          outlineColor="#ffffff"
          anchorX="center"
          anchorY="middle"
        >
          💬 タップして会話
        </Text>
      </Billboard>
    </group>
  );
}

// =============================================================================
// 5. 大阪ことば発見スポット（生活トリビア）
// =============================================================================
function CultureSpot({
  position,
  onInspect,
}: {
  position: [number, number, number];
  onInspect: () => void;
}) {
  const [hovered, setHovered] = useState(false);
  const signRef = useRef<THREE.Group>(null);

  useFrame(() => {
    if (signRef.current) {
      signRef.current.rotation.y = Math.sin(Date.now() * 0.002) * 0.1;
    }
  });

  return (
    <group
      ref={signRef}
      position={position}
      onClick={(e) => {
        e.stopPropagation();
        onInspect();
      }}
      onPointerEnter={() => setHovered(true)}
      onPointerLeave={() => setHovered(false)}
    >
      {/* 立て看板の脚 */}
      <mesh position={[0, 0.4, 0]} castShadow>
        <cylinderGeometry args={[0.04, 0.05, 0.8, 8]} />
        <meshStandardMaterial color="#4b5563" />
      </mesh>
      {/* 看板 */}
      <mesh position={[0, 0.8, 0]} castShadow>
        <boxGeometry args={[0.9, 0.6, 0.08]} />
        <meshStandardMaterial color={hovered ? "#fef08a" : "#fef9c3"} roughness={0.3} />
      </mesh>
      <Billboard position={[0, 0.8, 0.06]}>
        <Text fontSize={0.2} color="#b45309" outlineWidth={0.02} outlineColor="#fff">
          💡 大阪ことば
        </Text>
      </Billboard>
    </group>
  );
}

// =============================================================================
// 6. エリア固有のジオラマ装飾（プロシージャル）
// =============================================================================
function DioramaEnvironment({ areaId }: { areaId: AreaId }) {
  return (
    <group>
      {/* メインの地面（温かみのある緑の芝生ベース） */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.01, 0]} receiveShadow>
        <planeGeometry args={[28, 28]} />
        <meshStandardMaterial color="#7ec882" roughness={0.8} />
      </mesh>

      {/* 石畳の散策路 */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]} receiveShadow>
        <planeGeometry args={[3.4, 20]} />
        <meshStandardMaterial color="#f1e5d1" roughness={0.9} />
      </mesh>

      {/* エリアごとのアイコニックな装飾 */}
      {areaId === "airport" && (
        <group position={[-3.5, 0, -3]}>
          <mesh position={[0, 0.02, 0]} rotation={[-Math.PI / 2, 0, 0]}>
            <planeGeometry args={[2, 8]} />
            <meshStandardMaterial color="#334155" />
          </mesh>
          <mesh position={[0, 1.5, 0]} castShadow>
            <cylinderGeometry args={[0.4, 0.6, 3, 8]} />
            <meshStandardMaterial color="#e2e8f0" />
          </mesh>
          <mesh position={[0, 3.2, 0]}>
            <sphereGeometry args={[0.7, 8, 8]} />
            <meshStandardMaterial color="#38bdf8" roughness={0.1} />
          </mesh>
        </group>
      )}

      {areaId === "cafe" && (
        <group position={[-3, 0, 0]}>
          <mesh position={[0, 0.4, 0]} castShadow>
            <cylinderGeometry args={[0.7, 0.7, 0.06, 16]} />
            <meshStandardMaterial color="#b45309" />
          </mesh>
          <mesh position={[0, 0.2, 0]}>
            <cylinderGeometry args={[0.06, 0.06, 0.4, 8]} />
            <meshStandardMaterial color="#78350f" />
          </mesh>
          <mesh position={[0, 1.6, 0]} castShadow>
            <coneGeometry args={[1.2, 0.6, 12]} />
            <meshStandardMaterial color="#ef4444" />
          </mesh>
        </group>
      )}

      {areaId === "shop" && (
        <group position={[3.5, 0, -2]}>
          <mesh position={[0, 1.8, 0]} castShadow>
            <boxGeometry args={[1.8, 0.8, 0.2]} />
            <meshStandardMaterial color="#10b981" />
          </mesh>
          <mesh position={[0, 0.9, 0]}>
            <cylinderGeometry args={[0.08, 0.08, 1.8, 8]} />
            <meshStandardMaterial color="#94a3b8" />
          </mesh>
          <mesh position={[-1.2, 0.75, 0]} castShadow>
            <boxGeometry args={[0.7, 1.5, 0.5]} />
            <meshStandardMaterial color="#3b82f6" />
          </mesh>
        </group>
      )}

      {/* 周囲の木々 */}
      {[
        [-5, -4], [5, -5], [-4.5, 4], [4.5, 3.5], [-5.5, 0], [5.5, -1]
      ].map(([x, z], i) => (
        <group key={i} position={[x, 0, z]}>
          <mesh position={[0, 0.5, 0]} castShadow>
            <cylinderGeometry args={[0.15, 0.2, 1, 8]} />
            <meshStandardMaterial color="#854d0e" />
          </mesh>
          <mesh position={[0, 1.5, 0]} castShadow>
            <sphereGeometry args={[0.85, 12, 12]} />
            <meshStandardMaterial color={i % 2 === 0 ? "#4ade80" : "#22c55e"} roughness={0.6} />
          </mesh>
        </group>
      ))}

      {/* 街灯 */}
      {[[-2, -3], [2, 2]].map(([x, z], i) => (
        <group key={i} position={[x, 0, z]}>
          <mesh position={[0, 0.9, 0]} castShadow>
            <cylinderGeometry args={[0.05, 0.07, 1.8, 8]} />
            <meshStandardMaterial color="#475569" />
          </mesh>
          <mesh position={[0, 1.8, 0]}>
            <sphereGeometry args={[0.18, 8, 8]} />
            <meshStandardMaterial color="#fef08a" emissive="#fef08a" emissiveIntensity={0.8} />
          </mesh>
        </group>
      ))}
    </group>
  );
}

// =============================================================================
// メインコンポーネント: Area3DWorldField
// =============================================================================
export function Area3DWorldField({ areaData, areaId, onOpenUnit }: Props) {
  const { t, i18n } = useTranslation(["common", "learner"]);
  const { user } = useAuthStore();
  const mapArea = AREA_BY_ID[areaId];
  const { sections, units = [] } = areaData;
  const isJa = i18n.language === "ja";

  // 生徒アバターの位置
  const studentPos = useRef<[number, number, number]>([0, 0, 5.5]);
  const [targetPos, setTargetPos] = useState<[number, number, number]>([0, 0, 5.5]);

  // ダイアログ状態
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [cultureOpen, setCultureOpen] = useState<boolean>(false);
  const [activeUnitModal, setActiveUnitModal] = useState<{ sectionId: string; unitIndex: number; title: string } | null>(null);

  // セクションごとのピン配置（Z軸に沿ってゾーン分け配置）
  type PinItem = {
    unit: UnitSummary;
    section: SectionSummary;
    pos: [number, number, number];
    isCleared: boolean;
    isCurrent: boolean;
    isLocked: boolean;
  };

  const allPins: PinItem[] = [];
  const sectionHeaders: Array<{ section: SectionSummary; pos: [number, number, number] }> = [];

  // 全体で最初に見つかった未クリアのユニットを探す
  let foundCurrent = false;

  sections.forEach((sec, sIdx) => {
    const secUnits = units.filter((u) => u.section_id === sec.id).sort((a, b) => a.unit_index - b.unit_index);
    const zBase = 4.5 - sIdx * 6.5;

    // セクション看板位置
    sectionHeaders.push({
      section: sec,
      pos: [0, 0, zBase + 0.8],
    });

    secUnits.forEach((u, uIdx) => {
      const isCleared = u.completed;
      let isCurrent = false;
      let isLocked = false;

      if (!isCleared) {
        if (!foundCurrent) {
          isCurrent = true;
          foundCurrent = true;
        } else {
          isLocked = true;
        }
      }

      // カーブを描きながら配置
      const z = zBase - uIdx * 1.1;
      const x = Math.sin(uIdx * 1.3) * 0.95;

      allPins.push({
        unit: u,
        section: sec,
        pos: [x, 0, z],
        isCleared,
        isCurrent,
        isLocked,
      });
    });
  });

  function handleGroundClick(e: ThreeEvent<MouseEvent>) {
    const pt = e.point;
    setTargetPos([pt.x, 0, pt.z]);
  }

  const cultureInfo = OSAKA_CULTURE_NOTES[areaId] || OSAKA_CULTURE_NOTES.airport;

  return (
    <div className="relative w-full h-full min-h-[500px] flex-1 rounded-3xl overflow-hidden border-2 border-[#d9b45f]/40 shadow-2xl bg-gradient-to-b from-sky-100 via-emerald-50 to-amber-50 select-none">
      {/* 操作ガイドバー */}
      <div className="absolute top-3 left-3 right-3 z-30 flex items-center justify-between pointer-events-none">
        <div className="bg-white/95 backdrop-blur-md px-3.5 py-1.5 rounded-full border border-[#d9b45f]/30 shadow-sm flex items-center gap-2 pointer-events-auto">
          <span className="text-base">🎓</span>
          <span className="text-xs font-black text-[#8f332c]">
            {t("learner:world_guide_hint")}
          </span>
        </div>
      </div>

      {/* Three.js 3D ワールドキャンバス */}
      <Canvas
        shadows
        camera={{ position: [0, 9, 10], fov: 42 }}
        style={{ width: "100%", height: "100%" }}
      >
        <ambientLight intensity={1.15} />
        <directionalLight
          position={[6, 12, 6]}
          intensity={1.5}
          castShadow
          shadow-mapSize={[1024, 1024]}
          shadow-bias={-0.0001}
        />
        <directionalLight position={[-6, 8, -6]} intensity={0.5} color="#FFE4B5" />

        {/* クリック検出用プレーン */}
        <mesh
          rotation={[-Math.PI / 2, 0, 0]}
          position={[0, 0, 0]}
          onClick={handleGroundClick}
          visible={false}
        >
          <planeGeometry args={[45, 45]} />
          <meshBasicMaterial />
        </mesh>

        <Suspense fallback={null}>
          <DioramaEnvironment areaId={areaId} />

          {/* 生徒アバター */}
          <StudentAvatar
            targetPos={targetPos}
            currentPos={studentPos}
            studentName={user?.name || ""}
            onArrived={() => { }}
          />

          {/* セクション看板 */}
          {sectionHeaders.map((hdr) => (
            <SectionHeaderSign
              key={hdr.section.id}
              position={hdr.pos}
              title={isJa ? hdr.section.title_ja : hdr.section.title_en}
              index={hdr.section.index}
              cleared={hdr.section.cleared}
            />
          ))}

          {/* 道沿いに並ぶレッスンマス（ピン） */}
          {allPins.map((p) => (
            <LessonPin
              key={p.unit.id}
              unit={p.unit}
              position={p.pos}
              isCleared={p.isCleared}
              isCurrent={p.isCurrent}
              isLocked={p.isLocked}
              onSelect={() => {
                setActiveUnitModal({
                  sectionId: p.section.id,
                  unitIndex: p.unit.unit_index,
                  title: isJa ? p.section.title_ja : p.section.title_en,
                });
              }}
            />
          ))}

          {/* 住民NPC */}
          {mapArea && (
            <ResidentNPC
              position={[2.2, 0, 2.5]}
              name={isJa ? mapArea.residentNameJa : mapArea.residentNameEn}
              icon={mapArea.residentEmoji}
              onTalk={() => setDialogOpen(true)}
            />
          )}

          {/* 大阪ことば発見スポット */}
          <CultureSpot
            position={[-2.2, 0, 0]}
            onInspect={() => setCultureOpen(true)}
          />

          {/* 接地シャドウ */}
          <ContactShadows
            position={[0, 0.01, 0]}
            opacity={0.4}
            scale={22}
            blur={1.8}
            far={4}
            color="#2d3748"
          />
        </Suspense>

        <OrbitControls
          enableZoom={true}
          minDistance={5}
          maxDistance={16}
          minPolarAngle={Math.PI / 4.5}
          maxPolarAngle={Math.PI / 2.3}
          target={[0, 0.5, 0]}
        />
      </Canvas>

      {/* NPC会話モーダル */}
      <AnimatePresence>
        {dialogOpen && mapArea && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.92 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 15, scale: 0.95 }}
            className="absolute bottom-4 left-4 right-4 sm:left-12 sm:right-12 z-40 bg-[#fffdf7]/95 backdrop-blur-md rounded-2xl p-4 shadow-2xl border-2 border-[#d9b45f] flex items-center gap-3.5"
          >
            <div className="w-14 h-14 rounded-2xl bg-amber-100/80 border border-[#d9b45f] flex items-center justify-center text-3xl shrink-0 shadow-inner">
              {mapArea.residentEmoji}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-black text-[#8f332c] mb-0.5">
                {isJa ? mapArea.residentNameJa : mapArea.residentNameEn}（{isJa ? mapArea.residentRoleJa : mapArea.residentRoleEn}）
              </div>
              <p className="text-sm font-bold text-stone-800 leading-snug">
                {isJa ? (mapArea.residentGreetingJa || "今日も一緒に楽しく勉強しよう！") : (mapArea.residentGreetingEn || "Let's enjoy studying Japanese together today!")}
              </p>
            </div>
            <button
              onClick={() => setDialogOpen(false)}
              className="bg-[#8f332c] hover:bg-[#a63c34] text-white font-black text-xs px-3.5 py-2 rounded-xl transition active:scale-95 shadow shrink-0"
            >
              {t("learner:close")}
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 大阪ことば発見スポットモーダル */}
      <AnimatePresence>
        {cultureOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.92 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 15, scale: 0.95 }}
            className="absolute bottom-4 left-4 right-4 sm:left-12 sm:right-12 z-40 bg-gradient-to-r from-amber-50 via-yellow-50 to-orange-50 backdrop-blur-md rounded-2xl p-4 shadow-2xl border-2 border-amber-400 flex items-center gap-3.5"
          >
            <div className="w-14 h-14 rounded-2xl bg-amber-200 border border-amber-400 flex items-center justify-center text-3xl shrink-0 shadow-inner">
              💡
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-black text-amber-900 mb-0.5 flex items-center gap-1.5">
                <span>{t("learner:culture_spot")}</span>
                <span>•</span>
                <span className="font-bold">{cultureInfo.phrase}</span>
              </div>
              <p className="text-sm font-bold text-stone-800 leading-snug">
                {isJa ? cultureInfo.noteJa : cultureInfo.noteEn}
              </p>
            </div>
            <button
              onClick={() => setCultureOpen(false)}
              className="bg-amber-600 hover:bg-amber-700 text-white font-black text-xs px-3.5 py-2 rounded-xl transition active:scale-95 shadow shrink-0"
            >
              {t("learner:close")}
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* レッスン開始ポップアップモーダル */}
      <AnimatePresence>
        {activeUnitModal && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="absolute inset-0 z-50 bg-black/40 backdrop-blur-xs flex items-center justify-center p-4"
          >
            <div className="bg-[#fffdf7] rounded-3xl p-5 sm:p-6 shadow-2xl border-2 border-[#d9b45f] max-w-sm w-full text-center">
              <div className="w-16 h-16 rounded-full bg-amber-100 border border-[#d9b45f] text-3xl flex items-center justify-center mx-auto mb-3 shadow-inner">
                🎯
              </div>
              <h3 className="text-base sm:text-lg font-black text-[#8f332c] mb-1">
                {isJa ? `ユニット ${activeUnitModal.unitIndex}` : `Unit ${activeUnitModal.unitIndex}`}
              </h3>
              <p className="text-xs text-stone-600 font-bold mb-5">
                {activeUnitModal.title}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setActiveUnitModal(null)}
                  className="flex-1 py-2.5 rounded-xl text-xs font-black bg-stone-100 hover:bg-stone-200 text-stone-600 transition"
                >
                  {t("common:cancel")}
                </button>
                <button
                  onClick={() => {
                    onOpenUnit(activeUnitModal.sectionId, activeUnitModal.unitIndex);
                  }}
                  className="flex-1 py-2.5 rounded-xl text-xs font-black bg-[#8f332c] hover:bg-[#a63c34] text-white shadow-md transition active:scale-95"
                >
                  {t("learner:start_unit")} 🚀
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
